import { styled } from '@mui/material/styles';
import {
  Box,
  Paper,
  Button,
  TextField,
  Chip,
  Typography,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableCell,
  IconButton,
  TablePagination,
  FormControl
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { FORD_COLORS, FORD_TYPOGRAPHY, FORD_SPACING, FORD_RADIUS } from '../../theme/fordTheme.js';

// Common style patterns to reduce duplication
const COMMON_DEVICE_MANAGEMENT_STYLES = {
  baseContainer: {
    backgroundColor: FORD_COLORS.backgroundford,
    color: FORD_COLORS.textLight,
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    // border: `1px solid ${FORD_COLORS.primaryLight}`,
    borderRadius: FORD_RADIUS.lg,
    padding: FORD_SPACING.lg,
  },
  
  buttonBase: {
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    fontSize: FORD_TYPOGRAPHY.body2,
    fontWeight: FORD_TYPOGRAPHY.medium,
    textTransform: 'none',
    padding: '8px 16px',
    borderRadius: '8px',
    minWidth: '120px',
  },
  
  inputBase: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: FORD_COLORS.backgroundford,
      color: FORD_COLORS.textLight,
      borderRadius: '12px',
      fontSize: FORD_TYPOGRAPHY.body2,
      '& fieldset': {
        borderColor: '#233058',
      },
      '&:hover fieldset': {
        borderColor: '#4f7cff',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4f7cff',
      },
    },
    '& .MuiInputLabel-root': {
      color: FORD_COLORS.textMuted,
      fontSize: FORD_TYPOGRAPHY.body2,
      fontFamily: FORD_TYPOGRAPHY.fallbackFont,
      '&.Mui-focused': {
        color: '#4f7cff',
      },
    },
  },
  
  chipColors: {
    success: {
      backgroundColor: 'rgba(34, 197, 94, 0.2)',
      color: '#22c55e',
      border: '1px solid rgba(34, 197, 94, 0.3)',
    },
    warning: {
      backgroundColor: 'rgba(251, 191, 36, 0.2)',
      color: '#fbbf24',
      border: '1px solid rgba(251, 191, 36, 0.3)',
    },
    error: {
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      color: '#ef4444',
      border: '1px solid rgba(239, 68, 68, 0.3)',
    },
    default: {
      backgroundColor: 'rgba(156, 163, 199, 0.2)',
      color: '#9aa3c7',
      border: '1px solid rgba(156, 163, 199, 0.3)',
    }
  },
  
  dialogBase: {
    color: FORD_COLORS.textLight,
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  },
  
  textBase: {
    color: FORD_COLORS.textLight,
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    fontSize: FORD_TYPOGRAPHY.body1,
  },
  
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  
  hoverBlue: {
    '&:hover': {
      color: '#4f7cff',
    }
  },
  
  centeredMessage: {
    textAlign: 'center',
    padding: FORD_SPACING.lg,
  },
  
  borderStyle: {
    border: `1px solid ${FORD_COLORS.primaryLight}`,
  },
  
  spacingBase: {
    marginBottom: FORD_SPACING.md,
  },
  
  iconButton: {
    marginLeft: FORD_SPACING.xs,
    color: 'inherit'
  }
};

// Helper function to get chip styles
const getDeviceManagementChipStyles = (chiptype) => {
  switch (chiptype) {
    case 'chip-ok':
      return COMMON_DEVICE_MANAGEMENT_STYLES.chipColors.success;
    case 'chip-warn':
      return COMMON_DEVICE_MANAGEMENT_STYLES.chipColors.warning;
    case 'chip-error':
      return COMMON_DEVICE_MANAGEMENT_STYLES.chipColors.error;
    default:
      return COMMON_DEVICE_MANAGEMENT_STYLES.chipColors.default;
  }
};

// Main container - DeviceManagement specific
export const DeviceManagementContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  ...COMMON_DEVICE_MANAGEMENT_STYLES.baseContainer,
   backgroundColor: FORD_COLORS.backGroundColor,
  borderRadius: FORD_RADIUS.sm,
  width: '100%',
 padding: '0px',
  // overflow: 'hidden',
}));

// Outer container - DeviceManagement specific
export const DeviceManagementOuterContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100vw',
  margin: '0 auto',
  padding: '28px',
  // boxSizing: 'border-box',
  // overflow: 'hidden',
   backgroundColor: FORD_COLORS.backGroundColor,
}));

// Page header - DeviceManagement specific
export const DeviceManagementPageHeader = styled(Box)(({ theme }) => ({
  marginBottom: '22px', // Match OutagePageHeader spacing
  '& h2': {
    fontSize: FORD_TYPOGRAPHY.h4, // Match OutagePageHeader font size
    fontWeight: FORD_TYPOGRAPHY.extraBold, // Match OutagePageHeader font weight
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    color: 'rgb(19, 34, 52)', // Match OutagePageHeader color
    letterSpacing: FORD_TYPOGRAPHY.letterSpacingWide,
  },
  '& .subtitle': {
    color: 'rgb(94, 110, 126)', // Match OutagePageHeader subtitle color
    fontSize: FORD_TYPOGRAPHY.body2, // Match OutagePageHeader subtitle size
    marginTop: '4px', // Match OutagePageHeader subtitle spacing
    fontFamily: FORD_TYPOGRAPHY.newfont, // Match OutagePageHeader subtitle font
  },
}));

// Panel container - DeviceManagement specific
export const DeviceManagementPanel = styled(Paper)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.baseContainer,
     border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  marginBottom: FORD_SPACING.lg,
  fontFamily: FORD_TYPOGRAPHY.newfont,
}));

// Search panel - special styling for search form
export const DeviceManagementSearchPanel = styled(DeviceManagementPanel)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  marginBottom: '16px',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  flexWrap: 'wrap',
  border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '8px',
  },
}));

// Search input field - DeviceManagement specific
export const DeviceManagementSearchField = styled(TextField)(({ theme }) => ({
  flex: 1,
  ...COMMON_DEVICE_MANAGEMENT_STYLES.inputBase,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  '& .MuiOutlinedInput-input': {
    color: FORD_COLORS.textLight,
    padding: '10px 12px',
    fontSize: FORD_TYPOGRAPHY.body2,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontWeight: '400',
  },
  '& .MuiInputLabel-root': {
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontWeight: '400',
  },
  '& .MuiFormHelperText-root': {
    fontSize: '12px',
    fontFamily: FORD_TYPOGRAPHY.newfont,
  },
}));

// Primary button - DeviceManagement specific
export const DeviceManagementPrimaryButton = styled(Button)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.buttonBase,
  backgroundColor: '#4f7cff',
  color: FORD_COLORS.textColorWhite,
  '&:hover': {
    backgroundColor: '#3d63cc',
  },
  '&:disabled': {
    backgroundColor: '#2a2a2a',
    color: '#666',
  },
}));

// Secondary button - DeviceManagement specific
export const DeviceManagementSecondaryButton = styled(Button)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.buttonBase,
  backgroundColor: 'transparent',
  color: FORD_COLORS.textLight,
  border: `1px solid ${FORD_COLORS.primaryLight}`,
  '&:hover': {
    backgroundColor: `${FORD_COLORS.primaryLight}20`,
    borderColor: '#4f7cff',
  },
  '&:disabled': {
    borderColor: '#2a2a2a',
    color: '#666',
  },
}));

// Table container - DeviceManagement specific using Dashboard styling
export const DeviceManagementTableContainer = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  border: '1px solid #233058',
  // Hide scrollbar while maintaining scroll functionality
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari, Edge
  },
  msOverflowStyle: 'none', // Internet Explorer 10+
  '& table': {
    width: '100%',
    borderCollapse: 'collapse', // Match OutageTableContainer
    fontFamily: FORD_TYPOGRAPHY.newfont, // Match OutageTableContainer
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
       padding: '8px 10px',
      height: '42px',
      '& th': {
        backgroundColor: 'rgb(10, 37, 64)', // Match OutageTableContainer - Dark navy blue
        color: '#ffffff', // Match OutageTableContainer - White text
        fontWeight: 700, // Match OutageTableContainer - Bold weight
        fontSize: '12px', // Match OutageTableContainer - Smaller font
        fontFamily: FORD_TYPOGRAPHY.newfont, // Match OutageTableContainer
        padding: '8px 10px', // Match OutageTableContainer - Reduced padding
        textAlign: 'left',
        textTransform: 'uppercase', // Match OutageTableContainer - Capital letters
        letterSpacing: '0.5px', // Match OutageTableContainer - Better readability
        borderBottom: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 11,
      },
    },
    '& tbody': {
      position: 'relative',
      zIndex: 1,
      '& tr:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.05)', // Match OutageTableContainer hover
        cursor: 'pointer',
      },
      '& td': {
        color: FORD_COLORS.textMuted, // Match OutageTableContainer text color
        padding: '4px', // Match OutageTableContainer padding
        textAlign: 'left',
        fontSize: '12px', // Match OutageTableContainer font size
        fontFamily: FORD_TYPOGRAPHY.newfont, // Match OutageTableContainer font
        borderBottom: '1px solid rgba(35, 48, 88, 0.5)',
        backgroundColor: FORD_COLORS.backgroundford,
      },
    },
  },
}));

// Status switch - DeviceManagement specific
export const DeviceManagementStatusSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    color: FORD_COLORS.textMuted,
    '&.Mui-checked': {
      color: '#4caf50',
      '& + .MuiSwitch-track': {
        backgroundColor: '#4caf50',
        opacity: 0.5,
      },
    },
    '&.Mui-disabled': {
      color: '#666',
      '& + .MuiSwitch-track': {
        backgroundColor: '#333',
        opacity: 0.3,
      },
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: '#f44336',
    opacity: 0.5,
  },
}));

// Status chip - DeviceManagement specific with chiptype support like Dashboard
export const DeviceManagementStatusChip = styled(Chip)(({ theme, chiptype }) => ({
  ...getDeviceManagementChipStyles(chiptype),
  fontSize: '0.75rem',
  fontWeight: FORD_TYPOGRAPHY.medium,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  height: '24px',
  borderRadius: '12px',
}));

// Confirmation dialog - DeviceManagement specific
export const DeviceManagementConfirmDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: FORD_COLORS.backgroundford,
    color: FORD_COLORS.textLight,
    border: `1px solid ${FORD_COLORS.primaryLight}`,
    borderRadius: FORD_RADIUS.lg,
  },
}));

export const DeviceManagementDialogTitle = styled(DialogTitle)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.dialogBase,
 backgroundColor: 'rgb(10, 37, 64)', // Dark navy blue background matching OfflineEventsDrawer
  color: '#ffffff',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  fontSize: FORD_TYPOGRAPHY.h3,
  fontWeight: FORD_TYPOGRAPHY.bold,
  borderBottom: `1px solid ${FORD_COLORS.primaryLight}`,
  padding: FORD_SPACING.md, 
}));

export const DeviceManagementDialogContent = styled(DialogContent)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.textBase,
  padding: FORD_SPACING.lg,
  marginTop: '10px',
  fontFamily: FORD_TYPOGRAPHY.newfont,
}));

export const DeviceManagementDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: FORD_SPACING.md,
  borderTop: `1px solid ${FORD_COLORS.primaryLight}`,
  gap: FORD_SPACING.sm,
}));

// Loading message - DeviceManagement specific
export const DeviceManagementLoadingMessage = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.flexCenter,
  justifyContent: 'center',
  padding: FORD_SPACING.xl,
  color: FORD_COLORS.textMuted,
  fontSize: FORD_TYPOGRAPHY.body1,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  gap: FORD_SPACING.sm,
    border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
}));

// Error message - DeviceManagement specific
export const DeviceManagementErrorMessage = styled(Typography)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.textBase,
  color: '#f44336',
  textAlign: 'center',
  padding: FORD_SPACING.lg,
  backgroundColor: `#f4433620`,
  border: `1px solid #f44336`,
  borderRadius: FORD_RADIUS.sm,
  margin: FORD_SPACING.md,
}));

// Section header - DeviceManagement specific
export const DeviceManagementSectionHeader = styled(Typography)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.dialogBase,
  fontSize: '14px',
  fontWeight: FORD_TYPOGRAPHY.bold,
  marginBottom: FORD_SPACING.sm,
  marginTop: FORD_SPACING.sm,
  fontFamily: FORD_TYPOGRAPHY.newfont
}));

// Clickable device name
export const DeviceManagementClickableDeviceName = styled(Box)(({ theme }) => ({
  textDecoration: 'underline',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  fontWeight: 700, 
  color: 'rgb(19, 34, 52)', 
  fontSize:'12px',

  '&:hover': {
    color: '#3d63cc',
    textDecoration: 'underline',
    textDecorationThickness: '2px',
  },
}));

// Device MAC container
export const DeviceManagementDeviceMacContainer = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.flexCenter,
  gap: FORD_SPACING.xs,
}));

// Device MAC text
export const DeviceManagementDeviceMacText = styled('span')(({ theme }) => ({
  wordBreak: 'break-all'
}));

// Copy icon
export const DeviceManagementCopyIcon = styled(ContentCopy)(({ theme }) => ({
  fontSize: '16px',
  color: '#666',
  cursor: 'pointer',
  ...COMMON_DEVICE_MANAGEMENT_STYLES.hoverBlue,
}));

// Search results header
export const DeviceManagementSearchResultsHeader = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.flexCenter,
  justifyContent: 'space-between',
  // ...COMMON_DEVICE_MANAGEMENT_STYLES.spacingBase
  fontSize: '14px',
      fontFamily: FORD_TYPOGRAPHY.newfont
}));

// Refresh button
export const DeviceManagementRefreshButton = styled(IconButton)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.iconButton
}));

// Search field container
export const DeviceManagementSearchFieldContainer = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.spacingBase,
  width: '100%'
}));

// Global search field
export const DeviceManagementGlobalSearchField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    width: '100%',
    '& fieldset': {
      borderColor: FORD_COLORS.border,
    },
    '&:hover fieldset': {
      borderColor: FORD_COLORS.secondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: FORD_COLORS.secondary,
    },
  },
  '& .MuiInputBase-input': {
    color: FORD_COLORS.textMuted,
  },
  '& .MuiInputBase-input::placeholder': {
    color: FORD_COLORS.textSecondary,
    opacity: 1,
  },
}));

// Clear search icon
export const DeviceManagementClearSearchIcon = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.flexCenter,
  cursor: 'pointer',
  color: '#666',
  ...COMMON_DEVICE_MANAGEMENT_STYLES.hoverBlue,
}));

// Empty state cell
export const DeviceManagementEmptyStateCell = styled(TableCell)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.centeredMessage,
  padding: `${FORD_SPACING.xl} ${FORD_SPACING.md}`
}));

// Empty state text
export const DeviceManagementEmptyStateText = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.textMuted
}));

// Pagination container
export const DeviceManagementPaginationContainer = styled(TablePagination)(({ theme }) => ({
  color: 'inherit',
  '& .MuiTablePagination-toolbar': {
    color: 'inherit'
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    color: 'inherit'
  },
  '& .MuiSelect-select': {
    color: 'inherit'
  },
  '& .MuiIconButton-root': {
    color: 'inherit'
  }
}));

// Dialog content text
export const DeviceManagementDialogText = styled(Typography)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.spacingBase
}));

// Form control
export const DeviceManagementFormControl = styled(FormControl)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.spacingBase,
   fontFamily: FORD_TYPOGRAPHY.newfont,
}));

// Action menu item text
export const DeviceManagementActionMenuText = styled(Typography)(({ theme }) => ({
  fontWeight: 'medium'
}));

// Confirmation text
export const DeviceManagementConfirmationText = styled(Typography)(({ theme }) => ({
  marginTop: FORD_SPACING.xs,
  color: FORD_COLORS.textSecondary,
   fontFamily: FORD_TYPOGRAPHY.newfont,
}));

// Loading spinner container
export const DeviceManagementLoadingSpinner = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_MANAGEMENT_STYLES.flexCenter,
  gap: FORD_SPACING.sm,
}));
